import React from 'react';
import { List } from 'immutable';
import { FieldArray } from 'redux-form/immutable';
import { useSelector } from 'react-redux';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import type { WrappedFieldArrayProps } from 'redux-form/lib/FieldArray';
import { FormControl, FormHelperText, InputLabel } from '@material-ui/core';

import { useCommunitiesBriefQuery } from 'generated/graphql';
import FormValueSelector from 'retired/FormValueSelector';
import { Features } from 'utils/features';
import { selectUser } from 'entities/user/selectors';
import { Privileges } from 'utils/privileges';

export const useCommunitiesList = (): {
  communities: List<{ id: string; name: string; enabled?: boolean }>;
  loading: boolean;
} => {
  const user = useSelector(selectUser);

  const userCanViewCommunities =
    user?.hasFeature(Features.COMMUNITIES) && user?.hasPrivilege(Privileges.COMMUNITIES_VIEW);
  const userCanEditCommunities =
    user?.hasFeature(Features.COMMUNITIES) && user?.hasPrivilege(Privileges.COMMUNITIES_EDIT);

  const { data, loading } = useCommunitiesBriefQuery({
    variables: {
      includeDisabled: userCanEditCommunities || false,
    },
    skip: !userCanViewCommunities,
  });
  // Map communities data to the form that `FormValueSelector` would like
  const communities = List<{ id: string; name: string; enabled?: boolean }>(
    (data?.communities || []).map((c) => ({
      id: c.id,
      name: c.title,
      enabled: c.enabled === null || c.enabled,
    }))
  );

  return { communities, loading };
};

const SelectOne = ({
  fields,
  options,
}: {
  options: List<{ id: string; name: string; enabled?: boolean }>;
} & WrappedFieldArrayProps<string>): JSX.Element => (
  <FormControl fullWidth>
    <InputLabel>Organized by Community</InputLabel>
    <Select
      value={fields.get(0) || ''}
      onChange={(e) => {
        fields.removeAll();
        const { value } = e.target as { value: string | undefined };
        if (value) {
          fields.push(value);
        }
      }}
    >
      <MenuItem value={undefined}>
        <em>None</em>
      </MenuItem>
      {options.map(
        (c) =>
          c && (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
              {!c.enabled ? ' (Inactive)' : ''}
            </MenuItem>
          )
      )}
    </Select>
    <FormHelperText>
      Select a Community to have the impact of this deed count towards Community impact metrics
    </FormHelperText>
    <br />
    <br />
  </FormControl>
);

export const SelectOrganizingCommunity = ({
  communities,
}: {
  communities: List<{ id: string; name: string }>;
}): JSX.Element => (
  <>
    {communities.size > 0 && (
      <FieldArray
        name="organizingCommunityIds"
        component={SelectOne}
        options={communities}
        label="Organized by"
        input={{ name: 'organizingCommunity' }}
      />
    )}
  </>
);

export const SelectCommunities = ({
  communities,
}: {
  communities: List<{ id: string; name: string }>;
}): JSX.Element => (
  <>
    {communities.size > 0 && (
      <>
        <br />
        <br />
        <FieldArray
          name="communityIds"
          component={FormValueSelector}
          selectMultiple
          options={communities}
          labelText="Which communities match your Deed? (you may choose more than one)"
        />
      </>
    )}
  </>
);
